import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DescriptionIcon from '@mui/icons-material/Description';
import { api, imgUrl } from '../../api/api';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { key: 'business_license', label: 'Business License', color: '#551FFF' },
  { key: 'contract', label: 'Contract', color: '#1B5E3B' },
  { key: 'tax', label: 'Tax Document', color: '#C5A55A' },
  { key: 'insurance', label: 'Insurance', color: '#1565C0' },
  { key: 'lease', label: 'Lease Agreement', color: '#9C27B0' },
  { key: 'photo', label: 'Store Photo', color: '#FF6A00' },
  { key: 'other', label: 'Other', color: '#888' },
];

export default function StoreDocuments({ store }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('other');
  const [memo, setMemo] = useState('');

  useEffect(() => { if (store?._id) fetchDocs(); }, [store?._id]);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/store/documents/${store._id}`);
      setDocuments(data.documents || []);
    } catch (_) {}
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('content', file);
      formData.append('name', file.name);
      formData.append('category', category);
      formData.append('memo', memo);
      await api.post(`/store/documents/upload/${store._id}`, formData);
      toast.success('서류 업로드 완료');
      setMemo('');
      fetchDocs();
    } catch (err) {
      toast.error('업로드 실패: ' + (err.response?.data?.message || err.message));
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('이 서류를 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/store/documents/${docId}`);
      toast.success('삭제 완료');
      fetchDocs();
    } catch (_) { toast.error('삭제 실패'); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + 'B';
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + 'KB';
    return (bytes / (1024 * 1024)).toFixed(1) + 'MB';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <DescriptionIcon sx={{ color: '#551FFF', fontSize: 20 }} />
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>서류 관리</Typography>
        <Chip label={`${documents.length}건`} size="small" sx={{ fontSize: 11, height: 20 }} />
      </Box>

      {/* 업로드 영역 */}
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: 12 }}>카테고리</InputLabel>
            <Select value={category} label="카테고리" onChange={(e) => setCategory(e.target.value)}
              sx={{ fontSize: 12 }}>
              {CATEGORIES.map(c => (
                <MenuItem key={c.key} value={c.key} sx={{ fontSize: 12 }}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" placeholder="메모 (선택)" value={memo}
            onChange={(e) => setMemo(e.target.value)}
            sx={{ flex: 1, minWidth: 120, '& .MuiInputBase-root': { fontSize: 12 } }} />
          <Button component="label" variant="contained" size="small"
            startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
            disabled={uploading}
            sx={{ fontSize: 11, background: '#551FFF', borderRadius: 2, whiteSpace: 'nowrap' }}>
            파일 선택
            <input type="file" hidden onChange={handleUpload}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" />
          </Button>
        </Box>
      </Paper>

      {/* 서류 목록 */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
      ) : documents.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3, color: '#aaa', fontSize: 13 }}>
          등록된 서류가 없습니다.
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, color: '#888' } }}>
                <TableCell>카테고리</TableCell>
                <TableCell>파일명</TableCell>
                <TableCell>크기</TableCell>
                <TableCell>메모</TableCell>
                <TableCell>업로드일</TableCell>
                <TableCell align="center">액션</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => {
                const cat = CATEGORIES.find(c => c.key === doc.category) || CATEGORIES[6];
                return (
                  <TableRow key={doc._id} hover>
                    <TableCell>
                      <Chip label={cat.label} size="small" sx={{ fontSize: 10, height: 20, background: cat.color + '15', color: cat.color, fontWeight: 600 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.name || doc.fileName}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: '#888' }}>{formatSize(doc.fileSize)}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: '#555', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.memo || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: '#888' }}>
                      {new Date(doc.createdAt).toLocaleDateString('ko-KR')}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" href={`${imgUrl}${doc.fileUrl}`} target="_blank"
                        sx={{ color: '#551FFF' }}>
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(doc._id)}
                        sx={{ color: '#e53935' }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
